import { MessagingSdk } from 'lib-klab-sdks';
import { AuthenticatedUser } from '../../../domain/models/session/session';
import { GlobalContactUI } from '../../../domain/models/messaging/chat-ui.model';
import { groupMessageReactions, MessageReactionGroup } from '../../../domain/models/messaging/message-reaction.model';
import { colorForUserId, initialsFromName } from './chat-color.util';

const UNKNOWN_ACTOR_NAME = 'Usuario';
const UNKNOWN_ACTOR_COLOR = '#6B7280';

export interface MessageReactionActorViewModel {
  userId: number;
  displayName: string;
  initials: string;
  avatarColor: string;
  avatarUrl?: string;
  isCurrentUser: boolean;
  isUnknown: boolean;
}

export interface ResolvedMessageReaction extends MessagingSdk.MessageReaction {
  actor: MessageReactionActorViewModel;
}

export interface MessageReactionDetailsViewModel {
  total: number;
  currentUserId: number;
  rows: ResolvedMessageReaction[];
  groups: MessageReactionGroup[];
}

export interface MessageReactionActorContext {
  currentUserId: number;
  currentUser?: AuthenticatedUser | null;
  contacts?: readonly GlobalContactUI[] | null;
  participants?: readonly MessagingSdk.ParticipantSnapshot[] | null;
  messageSender?: MessagingSdk.SenderSnapshot | null;
}

/** Pure, read-only reaction derivation shared by every chat surface. */
export function deriveMessageReactionDetails(
  reactions: readonly MessagingSdk.MessageReaction[] | null | undefined,
  context: MessageReactionActorContext,
): MessageReactionDetailsViewModel {
  const deduplicated = deduplicateReactionRows(reactions);
  const rows = deduplicated.map((reaction) => ({
    ...reaction,
    actor: resolveReactionActor(reaction.userId, context),
  }));
  return {
    total: rows.length,
    currentUserId: context.currentUserId,
    rows,
    groups: groupMessageReactions(deduplicated, context.currentUserId),
  };
}

export function resolveReactionActor(
  userId: number,
  context: MessageReactionActorContext,
): MessageReactionActorViewModel {
  const isCurrentUser = userId === context.currentUserId;
  const participant = context.participants?.find((candidate) => candidate.userId === userId);
  const contact = context.contacts?.find((candidate) => candidate.userId === userId);
  const sender = context.messageSender?.userId === userId ? context.messageSender : undefined;
  const session = isCurrentUser && context.currentUser?.user_id === userId ? context.currentUser : undefined;

  const displayName = firstSafeDisplayName(userId,
    participant?.displayName,
    sender?.displayName,
    participant?.fullName,
    sender?.fullName,
    contact?.fullName,
    session?.username,
  );
  const avatarUrl = firstNonEmpty(participant?.photoUrl, sender?.photoUrl, contact?.avatarUrl);
  const isUnknown = !displayName;
  const resolvedName = displayName ?? UNKNOWN_ACTOR_NAME;

  return {
    userId,
    displayName: resolvedName,
    initials: initialsFromName(resolvedName) || 'U',
    avatarColor: isUnknown ? UNKNOWN_ACTOR_COLOR : contact?.color || colorForUserId(userId),
    ...(avatarUrl ? { avatarUrl } : {}),
    isCurrentUser,
    isUnknown,
  };
}

function deduplicateReactionRows(
  reactions: readonly MessagingSdk.MessageReaction[] | null | undefined,
): MessagingSdk.MessageReaction[] {
  const rows = new Map<string, MessagingSdk.MessageReaction>();
  for (const reaction of reactions ?? []) {
    if (!reaction?.emoji || !Number.isFinite(reaction.userId)) continue;
    const key = `${reaction.emoji}\u0000${reaction.userId}`;
    if (!rows.has(key)) rows.set(key, { ...reaction });
  }
  return Array.from(rows.values()).sort(
    (left, right) => compareEmoji(left.emoji, right.emoji) || left.userId - right.userId,
  );
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function firstSafeDisplayName(
  userId: number,
  ...values: Array<string | null | undefined>
): string | undefined {
  for (const value of values) {
    const candidate = sanitizeDisplayName(value, userId);
    if (candidate) return candidate;
  }
  return undefined;
}

function sanitizeDisplayName(
  value: string | null | undefined,
  userId: number,
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const normalizedId = String(userId);
  const compact = trimmed
    .normalize('NFKC')
    .toLocaleLowerCase('es')
    .replace(/[\s:_#-]+/g, '');
  if (/^\d+$/.test(compact)) return undefined;
  if (
    compact === normalizedId ||
    compact === `usuario${normalizedId}` ||
    compact === `user${normalizedId}` ||
    compact === `userid${normalizedId}`
  ) {
    return undefined;
  }

  return trimmed;
}

function compareEmoji(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
